<template>
    <div ref="container" class="col text-break message-container" v-bind:class="{ active: active, 'center-message': position === 'center' }">
        <template v-if="active">
            <div class="character">
                <span class="name" v-if="needCharacter && !showCharacterDialog">{{message.person || 'Please choose a character'}}</span>
                <fa-icon
                    v-on:click="$emit('toggle-fullscreen-message')"
                    icon="desktop"
                    class="toggle-fullscreen float-right clickable"
                    v-bind:class=" { 'text-white': isFullscreen }"
                ></fa-icon>
                <fa-icon
                    v-on:click="showImage = !showImage"
                    icon="image"
                    class="toggle-image float-right clickable"
                    v-bind:class="{ 'text-white': showImage }"
                    v-if="!needCharacter || hasCharacter"
                ></fa-icon>
                <fa-icon
                    v-on:click="$emit('toggle-emoji-picker', getEmojiPosition())"
                    icon="grin"
                    class="toggle-emoji float-right clickable"
                    v-if="hasCharacter"
                ></fa-icon>

                <fa-icon
                    icon="pen"
                    class="edit-character clickable"
                    v-on:click="showCharacterDialog = true"
                    v-if="needCharacter && !showCharacterDialog"
                ></fa-icon>
            </div>

            <image-upload
                v-if="showImage"
                v-on:image-loaded="hideImageUpload"
                :help="image_help"
            ></image-upload>
            <character-dialog
                v-if="needCharacter && showCharacterDialog"
                :snickId="snickId"
                v-on:select-character="characterSelected"></character-dialog>
            <div v-if="attrs.hasImage && !showImage" class="image-container">
                <span class="close-button clickable" aria-hidden="true" v-on:click="$emit('image-loaded')">&times;</span>
                <img :src="image" />
            </div>

            <div class="tiptap-editor">
                <editor-menu-bubble :editor="editor" :keep-in-bounds="keepInBounds" v-slot="{ commands, getMarkAttrs,isActive, menu }" >
                    <div
                        class="menububble"
                        :class="{ 'is-active': menu.isActive }"
                        :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
                    >
                        <button
                            class="menububble__button btn btn-outline-secondary"
                            :class="{ 'is-active': isActive.bold() }"
                            @click="commands.bold"
                        >
                            <fa-icon icon="bold" />
                        </button>
                        <button
                            class="menububble__button btn btn-outline-secondary"
                            :class="{ 'is-active': isActive.italic() }"
                            @click="commands.italic"
                        >
                            <fa-icon icon="italic" />
                        </button>
                        <button
                            class="menububble__button btn btn-outline-secondary"
                            :class="{ 'is-active': isActive.underline() }"
                            @click="commands.underline"
                        >
                            <fa-icon icon="underline" />
                        </button>


                        <form class="menububble__form" v-if="linkMenuIsActive" @submit.prevent="setLinkUrl(commands.link, linkUrl)">
                            <input class="menububble__input" type="text" v-model="linkUrl" placeholder="https://" ref="linkInput" @keydown.esc="hideLinkMenu"/>
                            <button class="menububble__button btn btn-outline-secondary" @click="setLinkUrl(commands.link, null)" type="button">
                                <fa-icon icon="times-circle" />
                            </button>
                        </form>

                        <template v-else>
                            <button
                                class="menububble__button btn btn-outline-secondary"
                                @click="showLinkMenu(getMarkAttrs('link'))"
                                :class="{ 'is-active': isActive.link() }"
                            >
                                <fa-icon icon="link" />
                            </button>
                        </template>

                    </div>
                </editor-menu-bubble>
                <editor-content
                    v-if="!needCharacter || message.person"
                    class="message"
                    ref="message"
                    :editor="editor"
                />
            </div>
        </template>
        <div v-if="!attrs.position" v-on:click="$emit('change-position', position)" class="slot-select">
            <fa-icon icon="plus"></fa-icon>
        </div>
    </div>
</template>

<script>
    import CharacterDialog from './CharacterDialog';
    import { Editor, EditorContent, EditorMenuBubble } from 'tiptap'
    import { Bold, Italic, Underline, Link, History } from 'tiptap-extensions'
    import _ from "lodash";

    export default {
        components: { CharacterDialog, EditorContent, EditorMenuBubble },
        props: [ 'message', 'attrs', 'position', 'snickId' ],
        watch: {
            message: {
                handler(newValue) {
                    if (!this.editor) return;
                    if (this.messagesArrayUpdated) {
                        // re render message
                        this.editor.setContent(newValue.message);
                        this.messagesArrayUpdated = false;
                    }
                },
                deep: true,
                immediate: true,
            },
        },
        methods: {
            getEmojiPosition() {
                const element = this.$refs.container.getBoundingClientRect();

                // Position emoji-picker horizontally
                let left = Math.floor(element.left);
                if (this.position === 'left') left = left + element.width;
                if (this.position === 'right') left = left - element.width - 28;

                // Position emoji-picker vertically
                const top = Math.floor(element.top);

                return {
                    id: this.message.id,
                    left,
                    top,
                    element: this.$refs.message,
                };
            },
            insertEmoji: function(emoji) {
                const transaction = this.editor.state.tr.insertText(emoji);
                this.editor.view.dispatch(transaction);
            },
            hideImageUpload(event) {
                this.showImage = false;
                this.$emit('image-loaded', event);
            },
            characterSelected(event) {
                // Close dialog
                this.showCharacterDialog = false;

                // Pass down a change-character event if a character is provided
                if (event) {
                    this.$emit('change-character', event);
                }
            },
            showLinkMenu(attrs) {
                this.linkUrl = attrs.href
                this.linkMenuIsActive = true
                this.$nextTick(() => {
                    this.$refs.linkInput.focus()
                })
            },
            hideLinkMenu() {
                this.linkUrl = null
                this.linkMenuIsActive = false
            },
            setLinkUrl(command, url) {
                command({ href: url })
                this.hideLinkMenu()
            },
        },
        computed: {
            active: function() {
                return this._props.position === this._props.attrs.position;
            },
            needCharacter: function() {
                return this._props.position !== 'center';
            },
            hasCharacter: function() {
                return this.message.person !== null;
            },
            isFullscreen: function() {
                return this.message.fullscreen;
            },
            image: function() {
                return '/storage/' + this.message.image;
            }
        },
        beforeDestroy() {
            this.editor.destroy()
        },
        mounted() {
            this.$root.$on('messages-updated', () => {
                this.messagesArrayUpdated = true;
            });
        },
        data: function () {
            return {
                showCharacterDialog: false,
                showImage: false,
                search: '',
                editor:  new Editor({
                    content: this.message.message,
                    extensions: [
                        new Bold(),
                        new Italic(),
                        new Underline(),
                        new Link(),
                        new History(),
                    ],
                    onUpdate: ({ getHTML, getJSON }) => {
                        const html = getHTML();
                        const json = getJSON();
                        let message = html;
                        if (json.content.length <= 1 && !json.content[0].content) {
                            // Message is only an empty paragraph
                            message = '';
                        }
                        this.$emit('input', message);
                    },
                }),
                keepInBounds: true,
                linkUrl: null,
                linkMenuIsActive: false,
                image_help: {
                    title: 'Properties for image upload',
                    body: [
                        'Width: 750 px, Height: Free',
                        'Format: JPG',
                        'Recommended file size: < 1 MB',
                        'Max file size: 2 MB',
                    ],
                },
            }
        },
    };
</script>
